import { useAppSelector } from '@hooks/useRedux';
import {
  selectGroupedOrderItems,
  selectIsOrderLoading,
  selectSelectedOrderTotals,
  selectSelectedOrder,
  selectSelectedTable,
  selectSelectedTakeAwayId,
} from '@store/runningOrder';
import { ArrowUpRight, Clock3, User } from 'lucide-react';
import React from 'react';
import { formatDate, formatTime, getFirstName } from '../../util';
import { LoadingAnimation } from '../common/LoadingAnimation';
import { OrderEmptyStateWrapper } from './OrderEmptyStateWrapper';
import { OrderItemsModal } from './OrderItemsModal';
import { OrderItemsWrapper } from './OrderItemsWraper';

export const OrderDetailPanel: React.FC = () => {
  const selectedTable = useAppSelector(selectSelectedTable);
  const selectedTakeAwayId = useAppSelector(selectSelectedTakeAwayId);
  const order = useAppSelector(selectSelectedOrder);
  const groupedItems = useAppSelector(selectGroupedOrderItems);
  const isLoading = useAppSelector(selectIsOrderLoading);
  const totals = useAppSelector(selectSelectedOrderTotals);

  const sectionHeaderClass = 'app-section-header';
  const createdAt = order?.created_at || '';
  const waiterName = getFirstName(order?.waiter_name);

  return (
    <div className="flex h-full flex-col bg-white text-black">
      {order && (
        <div className="app-bg border-b border-gray-200">
          <div className="px-4 pb-1 pt-3 text-center">
            <p className="text-xl font-semibold tracking-wide text-black">
              {order.table_name} #{order.bill_number}
            </p>
          </div>

          <div className="flex items-center justify-center gap-2 px-4 py-2 text-base text-black">
            <span>{formatDate(createdAt)}</span>
            <Clock3 size={15} />
            <span>{formatTime(createdAt)}</span>
            <User size={15} />
            <span>{waiterName || 'N/A'}</span>
            <ArrowUpRight size={14} />
          </div>
        </div>
      )}

      <div className="app-bg flex h-11 items-center justify-between border-b border-gray-200 px-4">
        <span className={sectionHeaderClass}>Item Name</span>
        <span className={sectionHeaderClass}>Amount</span>
      </div>

      <div className="relative flex-1 overflow-y-auto bg-white">
        {!selectedTable && !selectedTakeAwayId ? (
          <OrderEmptyStateWrapper message="Select a table or order" />
        ) : isLoading ? (
          <LoadingAnimation message="Loading order details..." />
        ) : !order ? (
          <OrderEmptyStateWrapper message="No order selected" />
        ) : groupedItems.length === 0 ? (
          <OrderEmptyStateWrapper message="No items in order" />
        ) : (
          <OrderItemsWrapper />
        )}
      </div>

      {totals.hasDiscount ? (
        <div className="app-bg mt-auto space-y-1 border-t border-gray-200 px-4 py-2">
          <div className="flex items-center justify-between text-xs text-black/80">
            <span>Net</span>
            <span>{totals.subtotal.toLocaleString()}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-black/80">
            <span>Discount</span>
            <span>-{totals.discount.toLocaleString()}</span>
          </div>
          <div className="flex items-center justify-between text-base font-semibold text-black">
            <span>Final Amount</span>
            <span>{totals.finalAmount.toLocaleString()}</span>
          </div>
        </div>
      ) : (
        <div className="app-bg mt-auto flex h-11 items-center justify-between border-t border-gray-200 px-4">
          <span className="text-base font-semibold text-black">Total</span>
          <span className="text-base font-semibold text-black">
            {totals.finalAmount.toLocaleString()}
          </span>
        </div>
      )}

      <OrderItemsModal />
    </div>
  );
};
